import { getStoredItem, getTwinkleDeviceId } from '~/helpers/userDataHelpers';
import { recoverFromLazyImportLoadError } from '~/helpers/lazyImportHelpers';
import { clientVersion } from '~/constants/defaultValues';

type RequestHelperModuleName =
  | 'build'
  | 'chat'
  | 'chess'
  | 'community'
  | 'content'
  | 'interactive'
  | 'management'
  | 'mission'
  | 'notification'
  | 'user'
  | 'zero';

interface RequestHelperContext {
  auth: typeof auth;
  handleError: (error: unknown) => Promise<never>;
  token: typeof token;
}

type RequestHelperFactory = (
  context: RequestHelperContext
) => Record<string, any>;

interface RequestHelperModule {
  default: RequestHelperFactory;
}

const token = () => getStoredItem('token');

const auth = () => ({
  headers: {
    authorization: token(),
    'x-twinkle-device-id': getTwinkleDeviceId(),
    'x-twinkle-client-version': clientVersion
  }
});

const helperModuleLoaders: Record<
  RequestHelperModuleName,
  () => Promise<RequestHelperModule>
> = {
  build: () => import('./build'),
  chat: () => import('./chat'),
  chess: () => import('./chess'),
  community: () => import('./community'),
  content: () => import('./content'),
  interactive: () => import('./interactive'),
  management: () => import('./management'),
  mission: () => import('./mission'),
  notification: () => import('./notification'),
  user: () => import('./user'),
  zero: () => import('./zero')
};

const helperMethodModules: Record<string, RequestHelperModuleName> =
  Object.create(null);

function registerMethods(
  moduleName: RequestHelperModuleName,
  methodNames: string[]
) {
  for (const methodName of methodNames) {
    helperMethodModules[methodName] = moduleName;
  }
}

registerMethods('build', [
  'loadBuildRewardSettings',
  'requestBuildRewardReview',
  'loadBuildRewardReview',
  'loadBuildRewardReviews',
  'decideBuildRewardReview',
  'loadBuildRewardProposal',
  'respondToBuildRewardProposal',
  'openBuildRewardReviewWorkspace',
  'proposeBuildRewardReviewChanges',
  'loadBuildRewardReviewProposal',
  'openBuildRewardProposalPreview',
  'requestBuildRewardProposalRewardPreview',
  'requestBuildRewards',
  'requestBuildRewardPreview',
  'requestBuildCardCraft',
  'requestBuildCardCraftPreview',
  'requestBuildCraft',
  'loadCardCraftBadges',
  'loadRewardEarnHub',
  'loadRewardEarnStandings',
  'recordEarnImpressions',
  'loadBuildWorkshopStatus',
  'cancelBuildWorkshopJob',
  'createBuild',
  'loadBuild',
  'loadBuildPublishedVersion',
  'loadBuildForkHistory',
  'loadBuildForkers',
  'loadBuildTeamMembers',
  'loadBuildBranch',
  'loadBuildRuntimeUploads',
  'deleteBuildRuntimeUpload',
  'loadRuntimeBuild',
  'loadBuildAppMcpSession',
  'connectBuildAppMcpRuntime',
  'pollBuildAppMcpCall',
  'completeBuildAppMcpCall',
  'createBuildAppMcpPairing',
  'setBuildAppMcpAllowEdits',
  'closeBuildAppMcpSession',
  'updateBuildMetadata',
  'loadBuildTabThumbnails',
  'loadBuildThumbnailOptions',
  'uploadBuildThumbnail',
  'captureBuildThumbnailPreview',
  'generateBuildThumbnail',
  'deleteBuildChatMessage',
  'routeBuildChatUpload',
  'createBuildChatAssistantNote',
  'createBuildChatUserNote',
  'createBuildChatReferenceNote',
  'createBuildChatReferenceDocuments',
  'loadBuildReferenceDocuments',
  'deleteBuildReferenceDocument',
  'cleanupBuildChatReferenceUploads',
  'loadBuildChatReferenceApps',
  'prepareBuildChatAppReferences',
  'loadMyBuilds',
  'loadCollaboratingBuilds',
  'loadBuildActivity',
  'updateBuildActivityViewed',
  'loadTodayTopViewedBuild',
  'loadRecentlyUsedBuilds',
  'loadFavoriteBuilds',
  'updateBuildFavorite',
  'loadMyPublicBuildsForPinning',
  'loadUserBuilds',
  'loadBuildProjectFiles',
  'updateBuildProjectFiles',
  'createBuildPreviewSession',
  'reportLuminePreviewCaptureEvidence',
  'loadBuildProjectFileChangeLogs',
  'deleteBuild',
  'downloadBuildProjectArchive',
  'loadBuildAiPrompts',
  'callBuildRuntimeAiChat',
  'callBuildRuntimeAiImage',
  'loadBuildRuntimeAiImageStatus',
  'callBuildRuntimeAiMusic',
  'callBuildRuntimeAiAudio',
  'loadBuildRuntimeAiMusicEstimate',
  'callBuildRuntimeAiChatStream',
  'callBuildRuntimeAiDecision',
  'callBuildRuntimeAiObject',
  'getBuildTwinkleNews',
  'listBuildTwinkleNewsEditions',
  'getBuildTwinkleNewsEdition',
  'generateBuildTwinkleNews',
  'callBuildRuntimeCharacterChat',
  'callBuildRuntimeCharacterChatStream',
  'listBuildArtifacts',
  'listBuildArtifactVersions',
  'restoreBuildArtifactVersion',
  'publishBuild',
  'unpublishBuild',
  'purchaseBuildGenerationReset',
  'loadPublicBuilds',
  'generateBuildTags',
  'forkBuild',
  'updateBuildCollaboration',
  'updateBuildLumineChatVisibility',
  'updateBuildLumineModelPreference',
  'loadBuildLumineChatHistory',
  'loadBuildContributors',
  'loadBuildContributionMembership',
  'inviteBuildContributor',
  'revokeBuildContributor',
  'acceptBuildContributorInvite',
  'declineBuildContributorInvite',
  'leaveBuildTeam',
  'loadMyBuildCollaborationRequest',
  'createBuildCollaborationRequest',
  'loadBuildCollaborationRequests',
  'requestBuildProjectLimitIncrease',
  'requestBuildStorageLimitIncrease',
  'reviewBuildProjectLimitRequest',
  'acceptBuildCollaborationRequest',
  'rejectBuildCollaborationRequest',
  'hideBuildCollaborationRequest',
  'cancelBuildCollaborationRequest',
  'createBuildContributionFork',
  'ensureDefaultBuildContributionBranch',
  'loadBuildContributions',
  'loadBuildContribution',
  'resetBuildContributionToMain',
  'updateBuildContributionFromMain',
  'loadBuildContributionMergeIntoMyBranch',
  'mergeBuildContributionIntoMyBranch',
  'mergeBuildContribution',
  'replaceMainWithBuildContribution',
  'loadBuildProjectLumineFix',
  'sponsorBuildProjectLumineFix',
  'loadBuildContributionLumineFix',
  'sponsorBuildContributionLumineFix',
  'applyBuildContributionLumineFix',
  'notifyBuildOwnerOfContribution',
  'suggestBuildThumbnailToOwner',
  'adoptBuildThumbnailSuggestion',
  'suggestBuildTitleToOwner',
  'adoptBuildTitleSuggestion',
  'declineBuildOwnerSuggestion',
  'replaceBuildContributionIntoMyBranch',
  'completeBuildContributionMerge',
  'loadBuildContributionForumThreads',
  'markBuildContributionForumViewed',
  'createBuildContributionForumThread',
  'loadBuildContributionForumThread',
  'createBuildContributionForumReply',
  'deleteBuildContributionForumThread',
  'deleteBuildContributionForumReply',
  'likeBuildContributionForumThread',
  'likeBuildContributionForumReply',
  'queryViewerDb',
  'execViewerDb',
  'getBuildApiToken',
  'uploadBuildRuntimeClip',
  'getBuildRuntimeClipStatus',
  'listBuildRuntimeClips',
  'getBuildMediaEnergy',
  'startBuildLiveSession',
  'markBuildLiveSessionStarted',
  'listBuildLiveSessions',
  'listBuildLiveHostSessions',
  'getBuildLiveSessionStatus',
  'joinBuildLiveSession',
  'leaveBuildLiveSession',
  'stopBuildLiveSession',
  'listBuildLiveReplays',
  'getBuildLiveReplayStatus',
  'joinBuildLiveReplay',
  'leaveBuildLiveReplay',
  'deleteBuildLiveReplay',
  'uploadBuildRuntimeFiles',
  'listBuildRuntimeFiles',
  'deleteBuildRuntimeFile',
  'getBuildApiUser',
  'getBuildApiUsers',
  'getBuildDailyReflections',
  'getBuildMySubjects',
  'searchBuildSubjects',
  'listBuildAiCards',
  'searchBuildAiCards',
  'getBuildAiCard',
  'listBuildAiStories',
  'listBuildAiStoryChapters',
  'searchBuildAiStories',
  'getBuildAiStory',
  'listBuildGrammarblesQuestions',
  'checkBuildGrammarblesAnswer',
  'getBuildGrammarblesHistory',
  'getBuildMinecraftData',
  'getBuildSubject',
  'getBuildSubjectComments',
  'listBuildSubjectComments',
  'getBuildContentWriteStatus',
  'createBuildContentSubject',
  'editBuildContentSubject',
  'createBuildContentComment',
  'editBuildContentComment',
  'getBuildProfileComments',
  'getBuildProfileCommentIds',
  'getBuildProfileCommentsByIds',
  'getBuildProfileCommentCounts',
  'getSharedDbTopics',
  'createSharedDbTopic',
  'getSharedDbEntries',
  'getSharedDbEntriesByIds',
  'addSharedDbEntry',
  'addSharedDbEntries',
  'updateSharedDbEntry',
  'deleteSharedDbEntry',
  'deleteSharedDbEntries',
  'claimSharedDbEntry',
  'getSharedDbKvItem',
  'listSharedDbKvItems',
  'setSharedDbKvItems',
  'deleteSharedDbKvItem',
  'getBuildLeaderboard',
  'submitBuildLeaderboardScore',
  'listBuildChatRooms',
  'createBuildChatRoom',
  'listBuildChatMessages',
  'sendBuildChatMessage',
  'deleteBuildRuntimeChatMessage',
  'getPrivateDbItem',
  'listPrivateDbItems',
  'setPrivateDbItem',
  'comparePrivateDbItem',
  'requestBuildArena',
  'requestBuildSoundtracks',
  'requestBuildNetwork',
  'requestBuildTown',
  'deletePrivateDbItem',
  'listBuildReminders',
  'createBuildReminder',
  'updateBuildReminder',
  'deleteBuildReminder',
  'getDueBuildReminders',
  'getBuildSubjectUpdateSubscription',
  'getBuildNotificationSubscription',
  'subscribeToBuildNotifications',
  'unsubscribeFromBuildNotifications',
  'subscribeToBuildNotificationsBatch',
  'unsubscribeFromBuildNotificationsBatch',
  'notifyBuildSubscribers',
  'subscribeToBuildSubjectUpdates',
  'unsubscribeFromBuildSubjectUpdates'
]);

registerMethods('community', [
  'loadCommunityFunds',
  'loadCommunityFundStats',
  'loadDonorLeaderboard'
]);

registerMethods('content', [
  'askParentGuide',
  'replaceSubjectAttachment',
  'replaceCommentAttachment',
  'addVideoToPlaylists',
  'checkDrafts',
  'saveFileData',
  'saveDraft',
  'deleteDraft',
  'addVideoView',
  'checkContentUrl',
  'decidePostIntent',
  'checkIfHomeOutdated',
  'checkIfUserResponded',
  'checkNumGrammarGamesPlayedToday',
  'closeContent',
  'deleteContent',
  'deletePlaylist',
  'editContent',
  'editPlaylistTitle',
  'editPlaylistVideos',
  'fetchPlaylistsContaining',
  'fetchUrlEmbedData',
  'updateUrlEmbedData',
  'createThumbnailUpload',
  'finishWatchingVideo',
  'likeContent',
  'batchSellAICards',
  'loadAICards',
  'loadFilteredAICards',
  'loadComments',
  'loadCommentsByPoster',
  'loadContent',
  'loadContinueWatching',
  'loadFeaturedPlaylists',
  'loadFeaturedSubjects',
  'loadFeeds',
  'recordContentView',
  'recordHomeFeedPerformance',
  'loadAIStoryTopic',
  'loadAIStoryClear',
  'loadAIStory',
  'loadAIStoryListeningAudio',
  'loadAIStoryListeningImage',
  'loadAIStoryQuestions',
  'loadAIStoryVocabSummary',
  'loadAIStoryVocabQuiz',
  'submitAIStoryVocabQuizAnswer',
  'uploadAIStoryAttempt',
  'loadAIStoryRankings',
  'generateAIStoryImage',
  'loadAIStoryImageStatus',
  'startGrammarSession',
  'checkGrammarAnswer',
  'cancelGrammarGame',
  'loadGrammarRankings',
  'loadGrammarReview',
  'challengeGrammarQuestion',
  'loadGrammarQuestState',
  'loadGrammarQuestRankings',
  'startGrammarQuestRun',
  'loadGrammarQuestRun',
  'answerGrammarQuestQuestion',
  'loadGrammarQuestMisses',
  'finishGrammarQuestRun',
  'loadLikedFeeds',
  'loadFeedsByUser',
  'loadPostsToReward',
  'loadPostsToRecommend',
  'loadHighXPSubjects',
  'markPostAsSkipped',
  'loadMorePlaylistList',
  'loadMoreNotableContents',
  'loadNotableContent',
  'loadNewFeeds',
  'countNewFeeds',
  'loadPlaylistList',
  'loadPlaylists',
  'loadPlaylistVideos',
  'loadReplies',
  'loadRightMenuVideos',
  'loadSubjects',
  'loadSubjectRewardLevels',
  'loadContentRewardLevel',
  'loadByUserUploads',
  'loadRecommendedUploads',
  'loadUploads',
  'makeThumbnailSecure',
  'loadVideoCurrentTime',
  'loadVideoWatchPercentage',
  'recommendContent',
  'reorderPlaylistVideos',
  'searchContent',
  'getHigherAICardBids',
  'searchAICardIds',
  'searchAICardStyles',
  'searchAICardWords',
  'setByUser',
  'updateCommentPinStatus',
  'updateRewardLevel',
  'checkCurrentlyWatchingAnotherVideo',
  'updateTotalViewDuration',
  'uploadComment',
  'sponsorAiEnergyCommentReply',
  'loadAiEnergyCommentReplySponsorStatus',
  'uploadContent',
  'uploadFeaturedPlaylists',
  'uploadFeaturedSubjects',
  'finishGrammarSession',
  'uploadQuestions',
  'uploadFile',
  'uploadPlaylist',
  'uploadSubject',
  'uploadThumb',
  'generateAIImage',
  'loadAIImageGenerationEstimate',
  'loadAIImageGenerationStatus',
  'loadAIImageResult',
  'getDailyQuestion',
  'submitDailyQuestionResponse',
  'recoverDailyQuestionSubmission',
  'shareDailyQuestionResponse',
  'shareDailyQuestionWithAI',
  'editDailyReflection',
  'refineDailyQuestionResponse',
  'simplifyDailyQuestion',
  'purchaseDailyQuestionRepair',
  'setDailyQuestionNextCategory',
  'setDailyQuestionCurrentFocus',
  'getDailyTrainingQuestion',
  'loadDailyReflectionModes',
  'loadReflectionStats',
  'setReflectionStatsVisibility',
  'loadUserReflectionStats',
  'getDailyQuestionHistory'
]);

registerMethods('chess', [
  'loadChessStats',
  'loadChessLevels',
  'updateChessCurrentLevel',
  'loadChessPuzzle',
  'submitChessAttempt',
  'recordChessAttemptResult',
  'loadChessDailyStats',
  'startTimeAttackPromotion',
  'submitTimeAttackAttempt',
  'adjustTimeAttackTimer',
  'unlockPromotion',
  'loadChessRankings'
]);

registerMethods('interactive', [
  'appendInteractiveSlide',
  'checkInteractiveNumUpdates',
  'deleteInteractiveSlide',
  'insertArchivedSlide',
  'insertInteractiveSlide',
  'undeleteInteractiveSlide',
  'loadInteractive',
  'editInteractiveSlide',
  'moveInteractiveSlide',
  'publishInteractive',
  'publishInteractiveSlide',
  'recoverArchivedSlide',
  'unPublishInteractiveSlide',
  'updateEmbedData',
  'uploadThumbForInteractiveSlide'
]);

registerMethods('notification', [
  'checkVersion',
  'fetchNotifications',
  'getBuildAppNotificationPreferences',
  'updateBuildAppNotificationPreferences',
  'loadBuildAppNotificationLaunchTarget',
  'fetchTodayStats',
  'checkUnansweredChess',
  'checkUnansweredOmok',
  'loadMoreNotifications',
  'loadMoreRewards',
  'loadRewards',
  'unlockDailyReward',
  'purchaseDailyTaskRepair',
  'clearUnavailableAIStoryDailyTask',
  'clearUnavailableDailyQuestion',
  'setDailyTaskRepairNoticeHidden',
  'setDailyTaskBoostStripCompact',
  'updateDailyRewardViewStatus',
  'loadDailyBonus',
  'postDailyBonus',
  'purchaseDailyTaskStreakRepair'
]);

registerMethods('management', [
  'loadBuildReviewRequests',
  'loadBuildReviewRequest',
  'decideBuildReviewRequest',
  'loadBuildCardCraftSettings',
  'requestBuildCardCraftReview',
  'addAccountType',
  'changeAccountType',
  'loadModerators',
  'loadModeratorsCSV',
  'loadWealthData',
  'loadWealthDataCSV',
  'loadAiCostReport',
  'loadAiCostEvents',
  'loadAiCostAccount',
  'loadAiCostAccountEvents',
  'loadAiCostReportCSV',
  'loadTwinkleAiCostsPaymentOverview',
  'setTwinkleAiCostsPaymentActive',
  'createTwinkleInfraCostsCheckoutSession',
  'createTwinkleAiCostsPortalSession',
  'loadAiCostRiskGroup',
  'loadAiCostRiskGroupEvents',
  'loadAiEnergyManualIdentityBuckets',
  'createAiEnergyManualIdentityBucket',
  'updateAiEnergyManualIdentityBucket',
  'saveAiEnergyManualIdentityRule',
  'disableAiEnergyManualIdentityRule',
  'addAiEnergyManualIdentityAccount',
  'addAiEnergyManualIdentityIpSignal',
  'setAiEnergyManualIdentityBucketBan',
  'migrateLegacyBansIntoBucket',
  'mergeAiEnergyManualIdentityBuckets',
  'loadAICardIssueReport',
  'loadHomeFeedPerformanceReport',
  'loadEffortLevelPerformanceReport',
  'loadGrammarPerformanceReport',
  'loadHomeFeedPerformanceExport',
  'loadPerformanceExport',
  'loadBuildWorldTelemetryReport',
  'loadBuildWorldTelemetryExport',
  'clearBuildWorldTelemetry',
  'loadNotableUsers',
  'loadNotableUsersCSV',
  'addNotableUser',
  'loadNotableSuggestions',
  'loadNotableReview',
  'dismissNotableSuggestion',
  'loadNotableUsersAnalytics',
  'loadNotableUserAnalytics',
  'clearHomeFeedPerformanceData',
  'addModerators',
  'addSupermods',
  'approveRequest',
  'changeSupermodRole',
  'checkDobApprovalSubmission',
  'checkMeetupApprovalSubmission',
  'convertUser',
  'deleteMessagePermanently',
  'deletePostPermanently',
  'grantAchievements',
  'loadAccountTypes',
  'deleteAccountType',
  'editAccountType',
  'loadAllAchievements',
  'loadApprovalItemById',
  'loadApprovalItems',
  'loadBannedUsers',
  'loadDeletedContent',
  'loadDeletedMessage',
  'loadDeletedPosts',
  'loadModificationItem',
  'loadSupermods',
  'retryDobApproval',
  'retryMeetupApproval',
  'revertApproval',
  'submitDobForApproval',
  'submitMeetupForApproval',
  'updateBanStatus',
  'loadAICardForImageRegeneration',
  'regenerateAICardImage'
]);

registerMethods('mission', [
  'approveGrammarQuestion',
  'deleteGrammarQuestion',
  'attachMissionTutorial',
  'checkMissionStatus',
  'editGrammarQuestion',
  'formatCode',
  'lintCode',
  'processAst',
  'loadGitHubData',
  'loadGrammarAttempts',
  'loadMoreGrammarAttempts',
  'loadGrammarCategories',
  'deleteGrammarCategory',
  'loadGrammarCategoryQuestions',
  'editGrammarCategory',
  'uploadGrammarCategory',
  'loadGrammarQuestions',
  'updateGrammarQuestionCategory',
  'loadMission',
  'updateMissionData',
  'loadMissionRankings',
  'loadMissionAttempts',
  'loadMissionAttemptsForPage',
  'loadMissionList',
  'loadMissionTypeIdHash',
  'loadGoogleMissionQuestions',
  'updateCurrentMission',
  'updateMissionStatus',
  'updateTutorialPrompt',
  'uploadGoogleQuestion',
  'approveGoogleQuestion',
  'disapproveGoogleQuestion',
  'deleteGoogleQuestion',
  'uploadGrammarQuestion',
  'uploadGrammarAttempt',
  'runSystemPromptPreview',
  'applySystemPromptToAIChat',
  'cloneSharedSystemPrompt',
  'loadSystemPromptProgress',
  'uploadMissionAttempt',
  'uploadMissionFeedback'
]);

registerMethods('chat', [
  'acceptInvitation',
  'blockUser',
  'loadBlockedUsers',
  'reportChatMessage',
  'unblockUser',
  'acceptTrade',
  'burnAICard',
  'buyAICard',
  'buyChatSubject',
  'buyChatTheme',
  'cancelAIMessage',
  'closeTransaction',
  'changeChannelOwner',
  'checkTransactionPossible',
  'checkChatAccessible',
  'createNewChat',
  'cancelChessRewind',
  'declineChessRewind',
  'deleteChatSubject',
  'deleteChatMessage',
  'deleteAIChatFile',
  'deleteTopic',
  'loadDeletedTopics',
  'restoreDeletedTopic',
  'permanentlyDeleteTopic',
  'bookmarkChatMessage',
  'unbookmarkChatMessage',
  'editTopic',
  'loadMyCustomInstructionTopics',
  'loadMySharedPrompts',
  'loadSharedPromptActivity',
  'updateTopicShareState',
  'cloneSharedTopic',
  'editCanChangeTopic',
  'editChannelSettings',
  'editChatMessage',
  'editWord',
  'fetchChessGameRecord',
  'fetchCurrentChessState',
  'fetchCurrentRewindRequest',
  'fetchCurrentOmokState',
  'getCustomInstructionsForTopic',
  'loadAIChatFiles',
  'improveCustomInstructions',
  'generateAICardImage',
  'loadAICardImageStatus',
  'getOffersForCard',
  'getOffersForCardByPrice',
  'getIncomingCardOffers',
  'getMyAICardOffers',
  'getVocabRouletteResult',
  'fetchWordMasterBreakStatus',
  'clearWordMasterBreak',
  'spinBreakPassRoulette',
  'loadWordMasterQuizQuestion',
  'submitWordMasterQuizAnswer',
  'postAICardOffer',
  'deleteAICardOffer',
  'hideAICardOffer',
  'unhideAICardOffer',
  'generateAICard',
  'getCurrentNextDayTimeStamp',
  'getNumberOfUnreadMessages',
  'loadChatChannelUnreadState',
  'hideChatAttachment',
  'hideChat',
  'inviteUsersToChannel',
  'leaveChannel',
  'removeMemberFromChannel',
  'listAICard',
  'delistAICard',
  'loadChat',
  'loadChatChannel',
  'loadChatMessage',
  'loadChatMessages',
  'loadAICardFeed',
  'loadAICardFeeds',
  'loadBookmarksForTopic',
  'loadMoreBookmarks',
  'loadMoreChannelMembers',
  'loadSubchannel',
  'loadPublicGroups',
  'loadGroupsForTrade',
  'loadBuildsForTrade',
  'searchGroups',
  'searchGroupsForTrade',
  'loadTopicMessages',
  'loadMoreRecentTopicMessages',
  'loadChatSubject',
  'loadDMChannel',
  'loadChatQuickAccess',
  'loadMoreChannels',
  'loadMoreChatMessages',
  'loadChatSubjects',
  'loadChatSubjectMessages',
  'loadMoreChatSubjects',
  'loadOtherUserTopics',
  'loadMoreOtherUserTopics',
  'loadSharedPrompt',
  'loadListedAICards',
  'loadMyListedAICards',
  'loadMyAICardCollections',
  'loadAICard',
  'loadPendingTransaction',
  'loadVocabularyFeeds',
  'loadVocabularyLeaderboards',
  'loadWordle',
  'loadWordleSkipShield',
  'loadWordleRankings',
  'loadWordleStreaks',
  'loadWordleDoubleStreaks',
  'reloadChatSubject',
  'lookUpWord',
  'pinChatTopic',
  'postChatReaction',
  'postTradeRequest',
  'putFavoriteChannel',
  'saveChatQuickAccess',
  'resetChatQuickAccess',
  'removeChatReaction',
  'rewindChessMove',
  'collectVocabulary',
  'getAiEnergyPolicy',
  'loadCallRelay',
  'loadAiUsageHistory',
  'purchaseAiEnergyRecharge',
  'saveChatMessage',
  'checkIfDuplicateWordleAttempt',
  'updateWordleAttempt',
  'searchChat',
  'searchChatMessages',
  'searchChatSubject',
  'searchUserToInvite',
  'searchChannelMembers',
  'sellAICard',
  'sendInvitationMessage',
  'setChessMoveViewTimeStamp',
  'startNewDMChannel',
  'syncLegacyChatReaction',
  'setOmokMoveViewTimeStamp',
  'updateLastChannelId',
  'updateChatLastRead',
  'updateSubchannelLastRead',
  'uploadChatTopic',
  'updateFeaturedTopic',
  'updateLastTopicId',
  'checkIfAIFileSupported',
  'uploadFileOnChat',
  'saveChatMessageWithFileAttachment',
  'loadChatPins',
  'updateChatPin',
  'loadChatPinContext'
]);

registerMethods('user', [
  'checkIfPasswordMatches',
  'checkIfUsernameExists',
  'changePassword',
  'changePasswordFromStore',
  'changeUsername',
  'updateImageGenerationSettings',
  'updateGrammarblesSettings',
  'updateImageEditorSettings',
  'confirmPassword',
  'deletePreviousUsername',
  'deleteProfilePictures',
  'deleteArchivedPicture',
  'editRewardComment',
  'resolveLumineRescue',
  'redeemLumineRescue',
  'loadFeaturedSubjectsOnProfile',
  'featureSubjectsOnProfile',
  'loadPinnedAICardsOnProfile',
  'loadPinnedBuildsOnProfile',
  'pinAICardsOnProfile',
  'pinBuildsOnProfile',
  'updateProfileSectionOrder',
  'revokeReward',
  'recordUserTraffic',
  'loadMyAchievements',
  'loadMeetupQuest',
  'loadMeetupSampleStories',
  'loadMeetupSampleStory',
  'loadMeetupStoryHall',
  'loadMeetupStoriesOverview',
  'loadMeetupStory',
  'loadUserMeetupStories',
  'loadMyMeetupStoryReveals',
  'markMeetupStoryRevealSeen',
  'loadMeetupStoryEditor',
  'startMeetupStory',
  'saveMeetupStory',
  'addMeetupStoryMedia',
  'updateMeetupStoryMedia',
  'removeMeetupStoryMedia',
  'reorderMeetupStoryMedia',
  'setMeetupStoryParentEmail',
  'requestMeetupStoryConsents',
  'submitMeetupStory',
  'reviewMeetupStory',
  'loadMeetupStoryConsent',
  'decideMeetupStoryConsent',
  'loadMyMeetupQuestSummary',
  'loadInviteCandidateStatuses',
  'loadMeetupKnownPeople',
  'askMeetupParent',
  'replyToMeetupParent',
  'nudgeMeetupBranch',
  'loadMeetupEmails',
  'saveMeetupEmail',
  'sendMeetupEmail',
  'discardMeetupEmail',
  'setMeetupEmailReview',
  'loadFriendsSwitch',
  'setFriendsSwitch',
  'loadFriendsOverview',
  'loadFriendStatus',
  'addFriend',
  'removeFriend',
  'loadMeetupOrgAsks',
  'setMeetupOrgAskStatus',
  'saveMeetupOrgRecipient',
  'composeMeetupOrgEmail',
  'loadMeetupParentBrief',
  'decideMeetupParentBrief',
  'suggestMeetupPlanChange',
  'reviseMeetupPlan',
  'loadMeetupBranchChoices',
  'loadMeetupBranch',
  'reviewMeetupBranch',
  'loadMeetupStaffSummary',
  'loadMeetupDesk',
  'loadCoordinatorApplications',
  'loadStaffApplication',
  'previewCoordinatorEmail',
  'decideMeetupPlan',
  'changeMeetupVenue',
  'confirmMeetupSlot',
  'addMeetupStaffNote',
  'resendCoordinatorEmail',
  'loadMeetupPublicCrew',
  'loadMeetupCrew',
  'updateMeetupCrewProfile',
  'setMeetupCrewOpen',
  'inviteToMeetupCrew',
  'cancelMeetupInvite',
  'acceptMeetupInvite',
  'declineMeetupInvite',
  'makeMeetupCrewFounder',
  'disbandMeetupCrew',
  'trackMeetupQuestView',
  'dismissMeetupNotice',
  'startMeetupCrew',
  'joinMeetupCrew',
  'leaveMeetupCrew',
  'removeMeetupCrewMember',
  'replaceMeetupCrewMember',
  'updateMyMeetupMembership',
  'setMeetupCrewAdult',
  'searchMeetupTeachers',
  'answerMeetupTeacherRequest',
  'submitMeetupPlan',
  'submitMeetupVideo',
  'reviewMeetupCrew',
  'requestMeetupReview',
  'decideMeetupInfoCheck',
  'setMeetupMemberBranch',
  'approveMeetupClass',
  'answerMeetupInfoCheck',
  'loadAchievementsByUserId',
  'loadUsersByAchievementId',
  'loadCoinHistory',
  'loadMyData',
  'loadUsernameHistory',
  'loadUserPictures',
  'loadUserTitles',
  'updateUserTitle',
  'loadKarmaPoints',
  'loadMonthlyLeaderboards',
  'loadMonthlyXp',
  'loadProfile',
  'loadProfileViaUsername',
  'loadRankings',
  'loadTodayRankings',
  'loadAllTodayRankings',
  'loadTop30TodayRankings',
  'loadUsers',
  'loadCoins',
  'loadXP',
  'loadMissionProgress',
  'loadXpAcquisition',
  'login',
  'recordLogout',
  'reorderProfilePictures',
  'reportError',
  'rewardUser',
  'searchUsers',
  'searchUsersWithAchievements',
  'sendVerificationEmail',
  'sendVerificationOTPEmail',
  'sendVerificationOTPEmailForSignup',
  'setDefaultSearchFilter',
  'setBuildQuickAccessMode',
  'setBuildHeaderCollapsed',
  'setLumineHeaderMinimized',
  'updateBuildStudioState',
  'updateNavTabsState',
  'setTheme',
  'signup',
  'createDevAccount',
  'toggleHideWatched',
  'toggleWordleStrictMode',
  'updateCollectType',
  'updateCurrentlyWatching',
  'collectRewardedCoins',
  'updateUserCoins',
  'updateUserXP',
  'uploadBio',
  'uploadGreeting',
  'uploadProfileInfo',
  'unlockAICardGeneration',
  'unlockUsernameChange',
  'unlockDonorLicense',
  'makeDonation',
  'loadChatNotificationSettings',
  'updateChatNotificationPreferences',
  'updateChatNotificationMute',
  'loadPushVapidKey',
  'savePushSubscription',
  'deletePushSubscription',
  'uploadUserPic',
  'updateUserPictureCaption',
  'updateUserPictures',
  'upgradeFileUploadSize',
  'upgradeNumPics',
  'upgradeRewardBoost',
  'verifyEmailViaOTP',
  'verifyEmailViaOTPForSignup',
  'verifyEmail',
  'getSignupInvite',
  'linkMinecraftInvite',
  'requestGuardianConsent',
  'getGuardianConsentStatus',
  'loadGuardianConsentReview',
  'decideGuardianConsent',
  'loadUserReferrals',
  'verifyPassphrase'
]);

registerMethods('zero', [
  'decideWebsiteAgentCard',
  'loadWebsiteAgentStarters',
  'generateVideoSubtitles',
  'splitSubtitles',
  'mergeSubtitles',
  'mergeVideoWithSubtitles',
  'textToSpeech'
]);

export default function requestHelpers(
  handleError: (error: unknown) => Promise<never>
) {
  const loadedModules: Partial<
    Record<RequestHelperModuleName, Promise<Record<string, any>>>
  > = {};
  const methodCache = new Map<string, (...args: any[]) => Promise<any>>();
  const baseHelpers: Record<string, any> = { auth };

  function loadHelperModule(moduleName: RequestHelperModuleName) {
    if (!loadedModules[moduleName]) {
      loadedModules[moduleName] = helperModuleLoaders[moduleName]()
        .then(({ default: factory }) =>
          factory({
            auth,
            handleError,
            token
          })
        )
        .catch(async (error) => {
          if (await recoverFromLazyImportLoadError(error)) {
            return await new Promise<Record<string, any>>(() => {});
          }
          throw error;
        });
    }
    return loadedModules[moduleName]!;
  }

  function getLazyHelper(methodName: string) {
    const cached = methodCache.get(methodName);
    if (cached) return cached;

    const moduleName = helperMethodModules[methodName];
    if (!moduleName) return undefined;

    const helper = async (...args: any[]) => {
      const helpers = await loadHelperModule(moduleName);
      const method = helpers[methodName];
      if (typeof method !== 'function') {
        throw new Error(`Request helper "${methodName}" is not available.`);
      }
      return method(...args);
    };
    methodCache.set(methodName, helper);
    return helper;
  }

  return new Proxy(baseHelpers, {
    get(target, prop) {
      if (typeof prop !== 'string') {
        return Reflect.get(target, prop);
      }
      if (prop === 'then') return undefined;
      if (Object.prototype.hasOwnProperty.call(target, prop)) {
        return target[prop as keyof typeof target];
      }
      return getLazyHelper(prop);
    },
    has(target, prop) {
      return (
        Object.prototype.hasOwnProperty.call(target, prop) ||
        (typeof prop === 'string' && prop in helperMethodModules)
      );
    },
    ownKeys(target) {
      return [...Reflect.ownKeys(target), ...Object.keys(helperMethodModules)];
    },
    getOwnPropertyDescriptor(target, prop) {
      if (Object.prototype.hasOwnProperty.call(target, prop)) {
        return Object.getOwnPropertyDescriptor(target, prop);
      }
      if (typeof prop === 'string' && prop in helperMethodModules) {
        return {
          configurable: true,
          enumerable: true
        };
      }
      return undefined;
    }
  });
}
